/**
 * OAuth Connection Hook
 *
 * React hook for managing OAuth authorization flows for connections.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from '@/auth/useAuth';
import { isArmMockMode } from '@/services/armConfig';
import {
  buildOAuthRedirectUrl,
} from '@/services/oauthService';
import { OAUTH_CHANNEL_NAME } from '@/services/oauthChannel';
import {
  createConnection,
  syncConnection,
  deleteConnection,
  fetchConnectionRuntimeUrl,
  testConnection,
  listConsentLinks,
  confirmConsentCode,
  type CreateConnectionParams,
  type ConnectionResource,
  type TestConnectionResult,
} from '@/services/connectionApi';
import { telemetryService } from '@/services/telemetryService';
import { useAppContextBuilder } from './useConnectionMutations';

// =============================================================================
// TYPES
// =============================================================================

/**
 * OAuth connection creation parameters
 */
export interface OAuthConnectionParams {
  /** Connection display name */
  connectionName: string;
  /** Connector ID (e.g., /subscriptions/.../managedApis/office365) */
  connectorId: string;
  /** Additional parameters for the connection */
  parameterValues?: Record<string, unknown>;
  /** Non-secret connection parameters to persist on the resource */
  connectionParameters?: Record<string, unknown>;
  /** Parameter set name (for multi-auth connectors) */
  parameterSetName?: string;
}

/**
 * OAuth re-authentication parameters (for existing connections)
 */
export interface OAuthReauthenticateParams {
  /** Name of the existing connection to re-authenticate */
  connectionName: string;
}

/**
 * OAuth connection flow state
 */
export type OAuthFlowState =
  | 'idle'
  | 'creating'
  | 'authenticating'
  | 'confirming'
  | 'syncing'
  | 'testing'
  | 'success'
  | 'error';

/**
 * Result from createOAuthConnection - includes both connection and any error
 * so callers don't have to rely on async state updates for error detection
 */
export interface OAuthConnectionResult {
  /** Created connection, or null if flow failed/cancelled */
  connection: ConnectionResource | null;
  /** Error message if flow failed (available synchronously, not via React state) */
  error?: string;
}

/**
 * Return type for useOAuthConnection hook
 */
export interface UseOAuthConnectionReturn {
  /** Current state of the OAuth flow */
  state: OAuthFlowState;
  /** Error message if flow failed */
  error: string | null;
  /** Created connection resource (if successful) */
  connection: ConnectionResource | null;
  /** Test result (if connection was tested) */
  testResult: TestConnectionResult | null;
  /** Initiate OAuth connection creation */
  createOAuthConnection: (params: OAuthConnectionParams) => Promise<OAuthConnectionResult>;
  /** Re-authenticate an existing OAuth connection */
  reauthenticateConnection: (params: OAuthReauthenticateParams) => Promise<boolean>;
  /** Cancel any ongoing OAuth flow */
  cancel: () => void;
  /** Reset state to idle */
  reset: () => void;
  /** Whether an OAuth flow is in progress */
  isInProgress: boolean;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook for creating OAuth-based connections
 *
 * Flow:
 * 1. Create connection resource in Azure (without auth)
 * 2. Initiate OAuth popup flow
 * 3. User authorizes in popup
 * 4. Confirm consent code
 * 5. Test connection
 */

// Polling constants (matching useReconnectConnection)
const POLLING_TIMEOUT_MS = 5 * 60 * 1000;
const POLLING_INTERVAL_MS = 500;

export function useOAuthConnection(): UseOAuthConnectionReturn {
  const [state, setState] = useState<OAuthFlowState>('idle');
  const [error, setErrorState] = useState<string | null>(null);
  const [connection, setConnection] = useState<ConnectionResource | null>(null);
  const [testResult, setTestResult] = useState<TestConnectionResult | null>(null);

  const { account, getOttoToken } = useAuth();
  const appContext = useAppContextBuilder();

  // Track if component is mounted for async operations
  const isMounted = useRef(true);
  const cancelledRef = useRef(false);
  // Mirror error state in a ref so we can access it synchronously in async functions
  const errorRef = useRef<string | null>(null);

  // Helper to set error in both state and ref
  const setError = useCallback((err: string | null) => {
    errorRef.current = err;
    setErrorState(err);
  }, []);
  const pollIntervalRef = useRef<number | null>(null);
  const isProcessingRef = useRef(false);
  const pollStartTimeRef = useRef<number>(0);
  const messageHandlerRef = useRef<((event: MessageEvent) => void) | null>(null);
  const popupRef = useRef<Window | null>(null);
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);

  // Helper to clean up polling interval, message listener, and popup
  const cleanupOAuthResources = useCallback(() => {
    if (pollIntervalRef.current !== null) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    isProcessingRef.current = false;
    if (messageHandlerRef.current) {
      window.removeEventListener('message', messageHandlerRef.current);
      messageHandlerRef.current = null;
    }
    if (popupRef.current) {
      try { popupRef.current.close(); } catch { /* ignore */ }
      popupRef.current = null;
    }
    if (broadcastChannelRef.current) {
      try { broadcastChannelRef.current.close(); } catch { /* ignore */ }
      broadcastChannelRef.current = null;
    }
  }, []);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      cleanupOAuthResources();
    };
  }, [cleanupOAuthResources]);

  /**
   * Run the OAuth popup flow - shared logic for both create and reauthenticate
   * Opens popup, waits for OAuth callback via postMessage/BroadcastChannel/polling
   * @returns OAuth code (string), empty string (Azure handled auth after reaching callback), or null (error/cancelled/timeout)
   */
  const runOAuthPopupFlow = useCallback(
    async (consentLink: string): Promise<string | null> => {
      // Open OAuth popup
      const popup = window.open(
        consentLink,
        'oauth-popup',
        'width=600,height=700,scrollbars=yes'
      );

      if (!popup) {
        return null; // Caller should handle popup blocked error
      }

      // Clean up any previous polling/listener state before starting new ones
      cleanupOAuthResources();
      popupRef.current = popup;
      pollStartTimeRef.current = Date.now();

      return new Promise<string | null>((resolve) => {
        // Track if we successfully reached the OAuth callback page.
        // This distinguishes "user closed popup before completing auth" from
        // "user completed auth and popup closed after callback".
        let reachedCallbackPage = false;

        const cleanup = () => {
          window.removeEventListener('message', messageHandler);
          messageHandlerRef.current = null;
          if (pollIntervalRef.current !== null) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          if (broadcastChannelRef.current) {
            try { broadcastChannelRef.current.close(); } catch { /* ignore */ }
            broadcastChannelRef.current = null;
          }
          isProcessingRef.current = false;
          try { popup.close(); } catch { /* ignore */ }
          popupRef.current = null;
        };

        const resolveWithCleanup = (code: string | null) => {
          cleanup();
          if (code !== null && code !== '') {
            isProcessingRef.current = true;
          }
          resolve(code);
        };

        // Listen for postMessage from OAuthCallbackPage (more reliable than URL polling)
        const messageHandler = (event: MessageEvent) => {
          if (event.origin !== window.location.origin) return;
          if (!event.data || typeof event.data !== 'object') return;
          const { type, code, error } = event.data;
          const isOAuthMessage = type === 'oauth-callback' || code !== undefined || error !== undefined;
          if (!isOAuthMessage) return;
          if (code !== undefined && typeof code !== 'string') return;
          if (error !== undefined && typeof error !== 'string') return;

          if (error) {
            resolveWithCleanup(null);
          } else {
            reachedCallbackPage = true;
            resolveWithCleanup(code ?? '');
          }
        };
        window.addEventListener('message', messageHandler);
        messageHandlerRef.current = messageHandler;

        // Also listen via BroadcastChannel (fallback for when COOP blocks window.opener)
        if ('BroadcastChannel' in window) {
          try {
            const channel = new BroadcastChannel(OAUTH_CHANNEL_NAME);
            channel.onmessage = (event: MessageEvent) => {
              const data = event.data;
              if (!data || typeof data !== 'object') return;
              const { type, code, error } = data;
              const isOAuthMessage = type === 'oauth-callback' || code !== undefined || error !== undefined;
              if (!isOAuthMessage) return;
              if (code !== undefined && typeof code !== 'string') return;
              if (error !== undefined && typeof error !== 'string') return;

              if (error) {
                resolveWithCleanup(null);
              } else {
                reachedCallbackPage = true;
                resolveWithCleanup(code ?? '');
              }
            };
            broadcastChannelRef.current = channel;
          } catch {
            // BroadcastChannel not supported
          }
        }

        pollIntervalRef.current = window.setInterval(() => {
          const elapsed = Date.now() - pollStartTimeRef.current;

          // Check for timeout
          if (elapsed > POLLING_TIMEOUT_MS) {
            resolveWithCleanup(null);
            return;
          }

          // Check if popup was closed by user
          try {
            if (popup.closed) {
              // If popup closed before reaching callback page, user cancelled
              // If popup closed after reaching callback, Azure handled auth
              resolveWithCleanup(reachedCallbackPage ? '' : null);
              return;
            }
          } catch {
            // COOP may block access
          }

          // Skip if already processing
          if (isProcessingRef.current) return;

          try {
            const popupUrl = popup.location.href;
            if (popupUrl.includes(window.location.origin)) {
              const urlParams = new URLSearchParams(new URL(popupUrl).search);
              const code = urlParams.get('code');
              const error = urlParams.get('error');

              if (code) {
                resolveWithCleanup(code);
                return;
              }

              if (error) {
                resolveWithCleanup(null);
                return;
              }

              if (popupUrl.includes('/oauth/callback')) {
                reachedCallbackPage = true;
                resolveWithCleanup('');
                return;
              }
            }
          } catch {
            // Cross-origin error is expected while user is on OAuth page
          }
        }, POLLING_INTERVAL_MS);
      });
    },
    [cleanupOAuthResources]
  );

  /**
   * Core OAuth flow - shared between create and reauthenticate
   * Handles: get consent link → popup → confirm → sync → test
   * @returns true on success, false on failure
   */
  const runOAuthFlowCore = useCallback(
    async (
      ctx: Awaited<ReturnType<typeof appContext.buildContext>>,
      connectionName: string,
      telemetryContext: { type: 'create' | 'reauthenticate'; connectorId?: string }
    ): Promise<boolean> => {
      if (!ctx || !account) return false;

      // Step 1: Initiate OAuth flow
      setState('authenticating');
      const redirectUrl = buildOAuthRedirectUrl();

      // Get consent link from Azure
      const { consentLink } = await listConsentLinks(ctx, {
        connectionName,
        redirectUrl,
        objectId: account?.localAccountId ?? account?.homeAccountId ?? 'local-user',
        tenantId: account?.tenantId ?? 'local-tenant',
      });

      // Step 2: Run OAuth popup flow (shared helper)
      const oauthCode = await runOAuthPopupFlow(consentLink);

      if (!isMounted.current || cancelledRef.current) return false;

      // null = user cancelled (closed popup), popup blocked, timeout, or explicit error
      if (oauthCode === null) {
        setError('Authorization was cancelled or failed. Please try again.');
        setState('error');
        return false;
      }

      // Step 3: Confirm consent code if we got one
      if (oauthCode) {
        setState('confirming');

        const freshToken = await getOttoToken();
        if (!freshToken && !isArmMockMode()) {
          setError('Failed to acquire token for consent confirmation');
          setState('error');
          return false;
        }

        await confirmConsentCode({ ...ctx, accessToken: freshToken as string }, {
          connectionName,
          code: oauthCode,
          objectId: account?.localAccountId ?? account?.homeAccountId ?? 'local-user',
          tenantId: account?.tenantId ?? 'local-tenant',
        });

        if (!isMounted.current || cancelledRef.current) return false;
      }

      // Step 4: Sync connection to finalize auth
      setState('syncing');
      await syncConnection(ctx, connectionName);

      if (!isMounted.current || cancelledRef.current) return false;

      // Step 5: Test connection (with retry — Azure needs time to propagate consent)
      setState('testing');
      let testResultData = await testConnection(ctx, connectionName);

      // Retry once after a delay if initial test fails (consent propagation delay)
      if (!testResultData.success && isMounted.current) {
        await new Promise((r) => setTimeout(r, 2000));
        if (!isMounted.current || cancelledRef.current) return false;
        testResultData = await testConnection(ctx, connectionName);
      }

      if (!isMounted.current || cancelledRef.current) return false;
      setTestResult(testResultData);

      if (!testResultData.success) {
        setError(testResultData.message ?? 'Connection test failed');
        setState('error');
        return false;
      }

      // Success!
      setState('success');
      const eventName = telemetryContext.type === 'create'
        ? 'connection.oauth.completed'
        : 'connection.oauth.reauthenticate.completed';
      telemetryService.trackEvent(eventName, {
        connectionName,
        ...(telemetryContext.connectorId && { connectorName: telemetryContext.connectorId }),
      });

      return true;
    },
    [account, appContext, runOAuthPopupFlow, getOttoToken, setError]
  );

  /**
   * Create an OAuth connection
   */
  const createOAuthConnection = useCallback(
    async (params: OAuthConnectionParams): Promise<OAuthConnectionResult> => {
      // Reset state
      cancelledRef.current = false;
      setError(null);
      setConnection(null);
      setTestResult(null);

      let createdConnection: ConnectionResource | null = null;

      try {
        // Build context
        const ctx = await appContext.buildContext();
        if (!ctx) {
          setState('error');
          return { connection: null, error: 'No app context available' };
        }

        // Get user info for OAuth
        if (!account) {
          const noUserError = 'No authenticated user found';
          setError(noUserError);
          setState('error');
          return { connection: null, error: noUserError };
        }

        // Step 1: Create connection resource (unique to create flow)
        setState('creating');
        telemetryService.trackEvent('connection.oauth.started', {
          connectorName: params.connectorId,
        });
        const createParams: CreateConnectionParams = {
          connectionName: params.connectionName,
          connectorId: params.connectorId,
          parameterValues: params.parameterValues ?? {},
          ...(params.parameterSetName && { parameterSetName: params.parameterSetName }),
          syncAfterCreation: false, // Defer sync until after OAuth flow completes
        };

        createdConnection = await createConnection(ctx, createParams);

        if (!isMounted.current || cancelledRef.current) {
          return { connection: null, error: 'Operation cancelled' };
        }
        
        setConnection(createdConnection);

        // Steps 2-6: Run shared OAuth flow (popup → confirm → sync → test)
        const success = await runOAuthFlowCore(ctx, params.connectionName, {
          type: 'create',
          connectorId: params.connectorId,
        });

        if (!success) {
          // OAuth flow failed (user cancelled, timeout, or test failed)
          // Clean up the connection resource we created
          telemetryService.trackEvent('connection.oauth.failed.deleteConnection');
          try {
            await deleteConnection(ctx, params.connectionName);
          } catch (deleteError) {
            console.warn('Failed to cleanup connection after OAuth failure', deleteError);
          }
          // Clear connection state so UI doesn't show stale connection
          setConnection(null);
          // Return the error that was set by runOAuthFlowCore via errorRef
          return { connection: null, error: errorRef.current ?? 'Authorization was cancelled or failed. Please try again.' };
        }

        // Step 7: Fetch connectionRuntimeUrl (unique to create flow)
        if (!createdConnection.properties.connectionRuntimeUrl) {
          const runtimeUrl = await fetchConnectionRuntimeUrl(ctx, createdConnection.id);
          if (runtimeUrl) {
            createdConnection.properties.connectionRuntimeUrl = runtimeUrl;
          }
        }

        return { connection: createdConnection };
      } catch (err) {
        if (!isMounted.current || cancelledRef.current) return { connection: null, error: 'Operation cancelled' };

        // Cleanup: delete connection on failure (unique to create flow)
        if (state !== 'success' && createdConnection) {
          telemetryService.trackEvent('connection.oauth.failed.deleteConnection');
          try {
            const ctx = await appContext.buildContext();
            if (ctx) {
              await deleteConnection(ctx, params.connectionName);
            }
          } catch (deleteError) {
            console.warn('Failed to delete connection after OAuth failure', deleteError);
          }
        }

        const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
        setError(errorMessage);
        setState('error');
        telemetryService.trackEvent('connection.oauth.failed', {
          connectorName: params.connectorId,
          error: errorMessage,
        });

        return { connection: null, error: errorMessage };
      }
    },
    [account, appContext, runOAuthFlowCore, state, setError]
  );

  /**
   * Re-authenticate an existing OAuth connection
   * Used for refreshing expired tokens or updating consent
   */
  const reauthenticateConnection = useCallback(
    async (params: OAuthReauthenticateParams): Promise<boolean> => {
      // Reset state
      cancelledRef.current = false;
      setError(null);
      setConnection(null);
      setTestResult(null);

      try {
        // Build context
        const ctx = await appContext.buildContext();
        if (!ctx) {
          setError('No app context available');
          setState('error');
          return false;
        }

        // Get user info for OAuth
        if (!account) {
          setError('No authenticated user found');
          setState('error');
          return false;
        }

        telemetryService.trackEvent('connection.oauth.reauthenticate.started', {
          connectionName: params.connectionName,
        });

        // Run shared OAuth flow (popup → confirm → sync → test)
        const success = await runOAuthFlowCore(ctx, params.connectionName, {
          type: 'reauthenticate',
        });

        return success;
      } catch (err) {
        if (!isMounted.current || cancelledRef.current) return false;

        const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
        setError(errorMessage);
        setState('error');
        telemetryService.trackEvent('connection.oauth.reauthenticate.failed', {
          connectionName: params.connectionName,
          error: errorMessage,
        });

        return false;
      }
    },
    [account, appContext, runOAuthFlowCore, setError]
  );

  /**
   * Cancel any ongoing OAuth flow
   */
  const cancel = useCallback(() => {
    cancelledRef.current = true;
    cleanupOAuthResources();
    setState('idle');
    setError('Flow cancelled');
  }, [cleanupOAuthResources, setError]);

  /**
   * Reset state to idle
   */
  const reset = useCallback(() => {
    setState('idle');
    setError(null);
    setConnection(null);
    setTestResult(null);
  }, [setError]);

  return {
    state,
    error,
    connection,
    testResult,
    createOAuthConnection,
    reauthenticateConnection,
    cancel,
    reset,
    isInProgress: state !== 'idle' && state !== 'success' && state !== 'error',
  };
}

// =============================================================================
// UTILITY HOOKS
// =============================================================================

/**
 * Hook for getting the OAuth redirect URL
 */
export function useOAuthRedirectUrl(): string {
  return buildOAuthRedirectUrl();
}

/**
 * Get descriptive text for OAuth flow state
 */
export function getOAuthStateMessage(state: OAuthFlowState): string {
  switch (state) {
    case 'idle':
      return 'Ready to connect';
    case 'creating':
      return 'Creating connection...';
    case 'authenticating':
      return 'Waiting for authorization...';
    case 'confirming':
      return 'Confirming authorization...';
    case 'syncing':
      return 'Syncing connection...';
    case 'testing':
      return 'Testing connection...';
    case 'success':
      return 'Connection created successfully!';
    case 'error':
      return 'Connection failed';
    default:
      return '';
  }
}